
import {
  MessageSquareQuote,
  BotMessageSquare,
  Send,
  Instagram,
  Twitch,
  Twitter,
  Github,
  MessageCircleMoreIcon,
  MailQuestion,
  Building2,
  Megaphone,
  Briefcase,
  LineChart,
  CircleUser,
  MapIcon,
  Share2,
  Facebook,
  Trophy,
  Globe,
  TrendingUp
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';

interface FooterProps {
  className?: string;
}

export const Footer = ({ className }: FooterProps) => {
  const location = useLocation();
  const isIndexPage = location.pathname === '/' || location.pathname === '/index';
  const isLeaguesPage = location.pathname === '/leagues';

  return (
    <footer className={cn(
      "bg-accent dark:bg-gray-800/50",
      isIndexPage ? "" : (isLeaguesPage ? "" : "mt-16"),
      "py-12",
      className
    )}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 className="font-semibold text-lg mb-4 dark:text-white">About Us</h3>
            <p className="text-secondary-light dark:text-gray-300 justify-center text-justify">
              Vote, Play, Discover. Your Opinion Shapes the Future. Turn Opinions into Impact.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-lg mb-4 dark:text-white">Business</h3>
            <ul className="space-y-2">
              <li>
                <a href="/business" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Register as Partner
                </a>
              </li>
              <li>
                <a href="/insights" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark flex items-center gap-2">
                  <LineChart className="w-4 h-4" />
                  Insights
                </a>
              </li>
              <li>
                <a href="#" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Trends
                </a>
              </li>
              <li>
                <a href="/business#testimonials" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark flex items-center gap-2">
                  <CircleUser className="w-4 h-4" />
                  Success Stories
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-lg mb-4 dark:text-white">Community</h3>
            <ul className="space-y-2">
              <li>
                <a href="/leagues"
                   className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark flex items-center gap-2">
                  <Trophy className="w-4 h-4"/>
                  Leagues
                </a>
              </li>
              <li>
                <a href="/user-journey"
                   className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark flex items-center gap-2">
                  <MapIcon className="w-4 h-4"/>
                  User Journey
                </a>
              </li>
              <li>
                <a href="/user-journey#private-users"
                   className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark flex items-center gap-2">
                  <MessageSquareQuote className="w-4 h-4"/>
                  Join Community
                  <Badge variant="outline"
                         className="text-xs ml-1 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">Soon</Badge>
                </a>
              </li>
              <li>
                <a href="/referral"
                   className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark flex items-center gap-2">
                  <Share2 className="w-4 h-4"/>
                  Referral Program
                </a>
              </li>
              <li>
                <a href="/supported-countries"
                   className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark flex items-center gap-2">
                  <Globe className="w-4 h-4"/>
                  Supported Countries
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-lg mb-4 dark:text-white">Contact</h3>
            <div className="flex gap-3 mb-4">
              <a href="https://x.com/blankpoll" target="_blank" rel="noopener noreferrer"
                 className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="https://www.reddit.com/user/blankpoll/" target="_blank" rel="noopener noreferrer" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark">
                <BotMessageSquare className="w-5 h-5" />
              </a>
              <a href="https://t.me/blankpollBot" target="_blank" rel="noopener noreferrer" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark">
                <Send className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61574803186933" target="_blank" rel="noopener noreferrer" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/blankpoll_official" target="_blank" rel="noopener noreferrer" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark">
                <Twitch className="w-5 h-5" />
              </a>
            </div>
            <ul className="space-y-2">
              <li className="text-secondary-light dark:text-gray-300 flex items-center gap-2">
                <MailQuestion className="w-4 h-4" />
                support@blankpoll.com
              </li>
              <li className="text-secondary-light dark:text-gray-300 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                business@blankpoll.com
              </li>
              <li className="text-secondary-light dark:text-gray-300 flex items-center gap-2">
                <Megaphone className="w-4 h-4" />
                press@blankpoll.com
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-accent-dark dark:border-gray-700">
          <p className="text-secondary-light dark:text-gray-400 text-xs leading-relaxed mb-6 text-justify">
            Our platform is an innovative poll and insight platform that enables users to scale purchasing behaviors through gamification. Our goal is to provide valuable market insights for influencers, startups and brands, supporting their strategic decision-making processes. We focus on delivering data-driven insights and helping users enhance their predictive abilities in an informed and engaging manner. With our real-time analytics and insight-driven approach, we provide a reliable environment for exploring market dynamics and making strategic decisions for the future.
          </p>
          <div className="flex flex-col md:flex-row-reverse justify-between items-center text-sm gap-4">
            <div className="flex gap-4 items-center text-center w-full md:w-auto justify-center">
              <a href="/terms" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark">
                Agreements
              </a>
              <a href="/whitepaper" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark">
                Whitepaper
              </a>
              <a href="/roadmap" className="text-secondary-light hover:text-primary dark:text-gray-300 dark:hover:text-primary-dark">
                Roadmap
              </a>
            </div>
            <p className="text-secondary-light dark:text-gray-400 text-center w-full md:w-auto order-last md:order-none">
              © 2025 blankpoll. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
